import { useCallback, useEffect, useMemo, useState } from 'react'
import { Autocomplete, TextField } from '@mui/material'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

// Others
import { FetchAllFeeTypesQuery, Maybe, PaginationPayload, useFetchAllFeeTypesLazyQuery } from '../../../generated'
import { GRAPHQL_QUERY_POLICY, PAGE_LIMIT } from '../../constants'

type FeeType = NonNullable<FetchAllFeeTypesQuery['fetchAllFeeTypes']['feeTypes']>[number]

interface FeeTypeDropdownProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  required?: boolean
  disabled?: boolean
  label?: string
  initialFeeTypes?: FeeType[]
  skipInitialFetch?: boolean
}

function FeeTypeDropdown<T extends FieldValues>({
  control,
  name,
  required,
  disabled = false,
  label = 'Fee Type',
  initialFeeTypes,
  skipInitialFetch = false
}: FeeTypeDropdownProps<T>) {
  const [feeTypes, setFeeTypes] = useState<FeeType[]>(initialFeeTypes || [])
  const [pagination, setPagination] = useState<PaginationPayload | null>({})
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')

  const [debouncedValue] = useDebounce(search, 500)

  // Update fee types when initialFeeTypes prop changes
  useEffect(() => {
    if (initialFeeTypes && initialFeeTypes.length > 0 && skipInitialFetch) {
      setFeeTypes(initialFeeTypes)
    }
  }, [initialFeeTypes, skipInitialFetch])
  const hasMorePages = useMemo(
    () => (currentPage || 0) < (pagination?.totalPages || 0),
    [currentPage, pagination?.totalPages]
  )

  const [fetchAllFeeTypes, { loading }] = useFetchAllFeeTypesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {
      setFeeTypes([])
    },
    onCompleted(data) {
      if (data) {
        const { fetchAllFeeTypes } = data
        const { feeTypes, pagination = {}, response } = fetchAllFeeTypes

        if (response?.status === 200) {
          setPagination(pagination)

          if (currentPage === 1) {
            setFeeTypes(feeTypes || [])
          } else {
            setFeeTypes(prev => [...prev, ...(feeTypes || [])])
          }
        }
      }
    }
  })

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget

      if (!listboxNode) return

      const { scrollTop, scrollHeight, clientHeight } = listboxNode
      const scrollThreshold = 0.9 // Load more when 90% scrolled

      if (scrollTop + clientHeight >= scrollHeight * scrollThreshold && !loading && hasMorePages) {
        setCurrentPage(prev => prev + 1)
      }
    },
    [loading, hasMorePages]
  )

  const getOptionLabel = useCallback(
    (option: FeeType | string) => {
      if (typeof option === 'string') {
        return feeTypes.find(feeType => feeType?.id === option)?.feeType || ''
      }

      return option?.feeType || ''
    },
    [feeTypes]
  )

  const isOptionEqualToValue = useCallback((option: FeeType, value: FeeType) => {
    return option?.id === value?.id
  }, [])

  const handleChange = useCallback((newValue: Maybe<string> | null) => {
    setSearch(newValue || '')
    setCurrentPage(1)
  }, [])

  useEffect(() => {
    if (skipInitialFetch && currentPage === 1 && debouncedValue === '' && initialFeeTypes) {
      // Skip fetch if we have initial data and it's the first page with no search
      return
    }
    fetchAllFeeTypes({
      variables: {
        fetchFeeTypes: {
          keyword: debouncedValue,
          paginationOptions: {
            limit: PAGE_LIMIT,
            page: currentPage
          }
        }
      }
    })
  }, [currentPage, fetchAllFeeTypes, debouncedValue, skipInitialFetch, initialFeeTypes])

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        return (
          <Autocomplete
            value={value}
            onChange={(_, newValue) => {
              onChange(newValue && 'id' in newValue ? newValue.id : null)
            }}
            inputValue={search}
            onInputChange={(_, newValue) => handleChange(newValue)}
            options={feeTypes}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            loading={loading && currentPage === 1}
            disabled={disabled}
            noOptionsText={loading ? 'Loading...' : 'No fee types found'}
            ListboxProps={{
              role: 'list-box',
              style: {
                maxHeight: '190px'
              },
              onScroll: handleScroll
            }}
            renderInput={params => (
              <TextField {...params} label={label} error={!!error} helperText={error?.message} required={!!required} />
            )}
          />
        )
      }}
    />
  )
}

export default FeeTypeDropdown
